import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Sun, CloudRain, Cloud, CloudSnow, CloudLightning, Drop, Wind, MapPin } from '@phosphor-icons/react';

// Open-Meteo weather codes → icon + label
const codeToMeta = (code) => {
  if (code === 0) return { label: 'Ciel clair', icon: Sun, color: '#F59E0B' };
  if ([1, 2, 3].includes(code)) return { label: code === 1 ? 'Peu nuageux' : code === 2 ? 'Partiellement nuageux' : 'Couvert', icon: Cloud, color: '#9CA3AF' };
  if ([45, 48].includes(code)) return { label: 'Brouillard', icon: Cloud, color: '#9CA3AF' };
  if ([51, 53, 55, 56, 57].includes(code)) return { label: 'Bruine', icon: Drop, color: '#3B82F6' };
  if ([61, 63, 65, 66, 67, 80, 81, 82].includes(code)) return { label: 'Pluie', icon: CloudRain, color: '#3B82F6' };
  if ([71, 73, 75, 77, 85, 86].includes(code)) return { label: 'Neige', icon: CloudSnow, color: '#60A5FA' };
  if ([95, 96, 99].includes(code)) return { label: 'Orage', icon: CloudLightning, color: '#7C3AED' };
  return { label: '—', icon: Cloud, color: '#9CA3AF' };
};

const STORAGE_KEY = 'batiboys_weather_location_v1';

export const WeatherWidget = () => {
  const [coords, setCoords] = useState(null);
  const [city, setCity] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const searchTimeout = useRef(null);

  // Load saved location
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const obj = JSON.parse(saved);
        setCoords({ lat: obj.lat, lon: obj.lon });
        setCity(obj.city || '');
        return;
      }
    } catch (e) { /* noop */ }
    // Otherwise geolocate
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lon = pos.coords.longitude;
          setCoords({ lat, lon });
          // Reverse geocode to get city name
          fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`)
            .then(r => r.json())
            .then(j => {
              const cityName = j?.address?.city || j?.address?.town || j?.address?.village || j?.address?.county || '';
              setCity(cityName);
              localStorage.setItem(STORAGE_KEY, JSON.stringify({ lat, lon, city: cityName }));
            })
            .catch(() => {});
        },
        () => {
          // Fallback Paris
          setCoords({ lat: 48.8566, lon: 2.3522 });
          setCity('Paris');
        },
        { timeout: 5000 }
      );
    } else {
      setCoords({ lat: 48.8566, lon: 2.3522 });
      setCity('Paris');
    }
  }, []);

  const fetchForecast = useCallback(async () => {
    if (!coords) return;
    setLoading(true);
    try {
      const u = `https://api.open-meteo.com/v1/forecast?latitude=${coords.lat}&longitude=${coords.lon}&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum,wind_speed_10m_max&timezone=auto&forecast_days=15`;
      const r = await fetch(u);
      const j = await r.json();
      setData(j);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [coords]);

  useEffect(() => { fetchForecast(); }, [fetchForecast]);

  const handleSearch = (q) => {
    setSearch(q);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (!q || q.length < 2) return;
    searchTimeout.current = setTimeout(async () => {
      try {
        const r = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(q)}&count=1&language=fr&format=json`);
        const j = await r.json();
        if (j?.results?.length) {
          const item = j.results[0];
          setCoords({ lat: item.latitude, lon: item.longitude });
          setCity(item.name);
          setSearch('');
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ lat: item.latitude, lon: item.longitude, city: item.name }));
        }
      } catch (e) { /* noop */ }
    }, 600);
  };

  const days = data?.daily?.time || [];

  return (
    <div className="card" data-testid="weather-widget" style={{ padding: '14px 16px', overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 8, color: '#1F2937' }}>
          <Sun size={18} color="#F59E0B" weight="fill" /> Météo 15 jours
          {city && <span style={{ fontSize: '0.8rem', color: '#5E5F66', fontWeight: 400 }}>· <MapPin size={12} style={{ display: 'inline', verticalAlign: -1 }} /> {city}</span>}
        </h3>
        <div style={{ position: 'relative', minWidth: 180 }}>
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Changer de ville…"
            data-testid="weather-city-input"
            style={{ height: 32, padding: '0 10px', border: '1px solid #E2E2E6', borderRadius: 6, fontSize: '0.82rem', width: '100%' }}
          />
        </div>
      </div>

      {loading || !data ? (
        <div style={{ padding: 30, textAlign: 'center', color: '#9CA3AF' }}>Chargement de la météo…</div>
      ) : (
        <div style={{ overflowX: 'auto' }} data-testid="weather-days">
          <div style={{ display: 'flex', gap: 8, paddingBottom: 4 }}>
            {days.map((dateStr, idx) => {
              const code = data.daily.weather_code[idx];
              const meta = codeToMeta(code);
              const Icon = meta.icon;
              const tmin = Math.round(data.daily.temperature_2m_min[idx]);
              const tmax = Math.round(data.daily.temperature_2m_max[idx]);
              const precip = data.daily.precipitation_sum[idx];
              const d = new Date(dateStr);
              const dayLabel = d.toLocaleDateString('fr-FR', { weekday: 'short' });
              const dayNum = d.getDate();
              const monthNum = d.getMonth() + 1;
              return (
                <div
                  key={dateStr}
                  data-testid={`weather-day-${idx}`}
                  style={{ minWidth: 78, flexShrink: 0, padding: '10px 8px', borderRadius: 8, background: idx === 0 ? '#EFF6FF' : '#F8FAFC', border: idx === 0 ? '1px solid #002FA7' : '1px solid #E2E8F0', textAlign: 'center' }}
                  title={meta.label}
                >
                  <div style={{ fontSize: '0.7rem', fontWeight: 700, color: idx === 0 ? '#002FA7' : '#5E5F66', textTransform: 'uppercase' }}>{dayLabel}</div>
                  <div style={{ fontSize: '0.75rem', color: '#9CA3AF', marginBottom: 4 }}>{String(dayNum).padStart(2, '0')}/{String(monthNum).padStart(2, '0')}</div>
                  <Icon size={28} weight="fill" color={meta.color} />
                  <div style={{ marginTop: 6, fontSize: '0.8rem' }}>
                    <strong style={{ color: '#1F2937' }}>{tmax}°</strong>
                    <span style={{ color: '#9CA3AF', marginLeft: 4 }}>{tmin}°</span>
                  </div>
                  {precip > 0.5 && (
                    <div style={{ fontSize: '0.65rem', color: '#3B82F6', marginTop: 2, display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                      <Drop size={10} weight="fill" /> {precip.toFixed(1)}mm
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default WeatherWidget;
